import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesLeft, faAnglesRight, faPlay } from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper";
import { actionContext } from "../../app/page";
import { CasinoImageInterface, exportNewCasinoJson, otherCasino } from "../../data/gameData_casino";
import { SlotImageInterface, exportNewSlotJson } from "../../data/gameData_slot";
import { loadingContext } from "../provider/game_loading_provider";
import { ErrorVendorCheck } from "../../util/ErrorVendorCheck";
import { UserContext } from "../../app/_root";
import AudioPlayer from "../layout/audio_player";
import { lobbyModalContext } from "../provider/lobby_modal_provider";
import MediaPlayer from "../layout/media_player";
import api from "../../util/api";
import { Player } from "@lottiefiles/react-lottie-player";
import loading from "../../_asset/lottie/loading.json";
import arrow from "../../_asset/etc/pop_arrow3.png";
import poppulartitlebg from "../../_asset/etc/zeus_title_bg.png";

import swal from "sweetalert"
import TitleHeader from '../layout/title_header';
import GameTitleHeader from '../layout/gameTitleHeader';

interface HighlightGameInterface {
    uid: string,
    title: string,
    vendor: string,
    thumbnail: string
}

export function PopularGameList() {
    const [popularGames, setPopularGames] = useState<HighlightGameInterface[]>()
    const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth)
    const { User } = useContext(UserContext)
    const { SetLoginPop } = useContext(actionContext)

    useEffect(() => {
        api('/api/game/games/highlight/')
            .then((res) => res.json())
            .then((data: HighlightGameInterface[]) => {
                setPopularGames(data)
            })
            .catch(() => setPopularGames([]))
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const currentSlidesPerView = (() => {
        if (viewportWidth < 512) return 2
        if (viewportWidth < 768) return 3
        if (viewportWidth < 1060) return 4
        if (viewportWidth < 1460) return 5
        return 6
    })()

    const visiblePopularGames = useMemo(() => {
        if (!popularGames || popularGames.length === 0) return []
        if (popularGames.length === 1) return popularGames

        let expanded = [...popularGames]
        while (expanded.length <= currentSlidesPerView) {
            expanded = [...expanded, ...popularGames]
        }
        return expanded
    }, [popularGames, currentSlidesPerView])

    const gameCount = visiblePopularGames.length
    const canSlide = gameCount > currentSlidesPerView
    const shouldLoop = gameCount >= currentSlidesPerView * 2

    const openGame = (game: HighlightGameInterface) => {
        if (!User?.is_authenticated) {
            SetLoginPop()
            return
        }

        window.open(
            `/gameLobby/${game.vendor}/?uid=${game.uid}&vendor=${game.vendor}`,
            `${game.vendor}_${game.uid}`,
            `width=1200 height=640`
        )
    }

    const getVendorLogo = (game: HighlightGameInterface) => {
        const matchedGame = exportNewSlotJson.find((slotGame) => {
            return slotGame.id === game.uid || slotGame.vendor === game.vendor
        })

        return matchedGame?.logo || ''
    }

    return (
        <section className={'popular-games'}>
            <header>
                <div className="inner">
                    <button className="popular-arrow left">
                        <img src={arrow} alt="prev" />
                    </button>
                    <div className="title-wrapper">
                        <img src={poppulartitlebg} alt="타이틀 배경" className="title-bg" />
                        <p className="title">인기게임</p>
                    </div>
                    <button className="popular-arrow right">
                        <img src={arrow} alt="next" />
                    </button>
                </div>
            </header>
            <main>
                <Swiper
                    key={`popular-${viewportWidth}-${gameCount}-${shouldLoop ? 'loop' : 'normal'}`}
                    className={'popular-game-swiper'}
                    slidesPerGroup={1}
                    spaceBetween={12}
                    loop={shouldLoop}
                    observer={true}
                    observeParents={true}
                    speed={700}
                    autoplay={canSlide ? {
                        delay: 1400,
                        disableOnInteraction: false,
                        reverseDirection: false,
                    } : false}
                    navigation={canSlide ? {
                        prevEl: '.popular-arrow.left',
                        nextEl: '.popular-arrow.right',
                    } : false}
                    allowTouchMove={canSlide}
                    watchOverflow={true}
                    breakpoints={{
                        0: { slidesPerView: 2 },
                        512: { slidesPerView: 3 },
                        768: { slidesPerView: 4 },
                        1060: { slidesPerView: 5 },
                        1460: { slidesPerView: 6 },
                    }}
                    modules={[Autoplay, Navigation]}
                >
                    {
                        visiblePopularGames.length > 0
                            ? visiblePopularGames.map((game, index) => {
                                const vendorLogo = getVendorLogo(game)

                                return (
                                    <SwiperSlide key={`${game.vendor}-${game.uid}-${index}`}>
                                        <button
                                            type={'button'}
                                            className={'popular-game-item'}
                                            onClick={() => openGame(game)}
                                        >
                                            <span className={'badge'}>인기</span>
                                            <img src={game.thumbnail} className={'game-image'} alt={game.title || game.vendor} />
                                            <div className={'overlay'}>
                                                <div className={'play'}>
                                                    <FontAwesomeIcon icon={faPlay} />
                                                </div>
                                            </div>
                                            <div className={'caption'}>
                                                {
                                                    vendorLogo
                                                        ? <img src={vendorLogo} alt={game.vendor} />
                                                        : <p className={'vendor-name'}>{game.vendor}</p>
                                                }
                                                <p>{game.title || game.vendor}</p>
                                            </div>
                                        </button>
                                    </SwiperSlide>
                                )
                            })
                            : [...Array(6)].map((_, index) => (
                                <SwiperSlide key={`popular-loading-${index}`}>
                                    <div className={'popular-game-item loading'}>
                                        <div className={'popular-loading'}>
                                            <Player
                                                src={loading}
                                                autoplay
                                                loop
                                                style={{ width: '8rem', height: '8rem' }}
                                            />
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))
                    }
                </Swiper>
            </main>
        </section>
    )
}

export function CasinoList() {
    const { User } = useContext(UserContext)
    const { SetLoginPop } = useContext(actionContext)
    // sessionStorage.setItem('test', '1')
    return (
        <div>
            {/* <TitleHeader type="casino">
                <p>라이브 카지노</p>
                <span>Live Casino</span>
            </TitleHeader> */}
            <GameTitleHeader type="casino" />
            <main className={"new-company-list casino"}>
                {
                    exportNewCasinoJson.map((comp, index) => {
                        return (
                            <CompanyItems key={index}
                                isLogin={User?.is_authenticated ? true : false}
                                SetLoginPop={SetLoginPop}
                                company={comp} />
                        )
                    })
                }
            </main>
        </div>
    );
}

function CompanyItems({ company, isLogin, SetLoginPop }: { company: CasinoImageInterface, isLogin: boolean, SetLoginPop: Function }) {
    const { SetLoadingPop } = useContext(loadingContext)
    const [audioPlay, setAudioPlay] = useState<boolean>(false)
    const { SetLobbyShow, SetVendor, SetType } = useContext(lobbyModalContext)

    return (
        <div
            onClick={e => {
                if (isLogin) {
                    if (otherCasino.includes(company.vendor)) {
                        SetVendor(company.vendor)
                        SetType('casino')
                        SetLobbyShow()
                    } else {
                        // SetLoadingPop(true)
                        // ErrorVendorCheck(company.vendor, company.id, 'casino')
                        //     .finally(()=> SetLoadingPop(false))
                        window.open(`/gameLobby/${company.vendor}/?type=casino&uid=${company.id}&vendor=${company.vendor}`,
                            `${company.vendor}_${company.id}`, `width=1200 height=640`)
                    }
                    // window.open(`/api/game/lobbies/${company.vendor}/${company.id}`, `lobby`,`popup,width=1200, height=600`)
                } else {
                    SetLoginPop()
                }
            }}>
            <div className={'hover-cover'}
                onMouseOver={e => setAudioPlay(true)}
                onMouseOut={e => setAudioPlay(false)}
            ></div>
            <div className='border-frame'></div>
            <div className='patterns'>
                <div className='girl'>
                    <img src={company.img} alt="" />
                </div>
                <div className='company-logo'>
                    <img src={company.logo} alt="" />
                </div>
                <div className='game-start'>
                    <button>게임시작</button>
                </div>
            </div>
            <AudioPlayer type={"company"} play={audioPlay} />
        </div>
    )
}

// **************************************************************************************
// **************************************************************************************
// **************************************************************************************

export function SlotList() {
    const { User } = useContext(UserContext)
    const { SetLoginPop } = useContext(actionContext)
    return (
        <div>
            {/* <TitleHeader type="slot">
                <p>슬롯머신</p>
                <span>slot machine</span>
            </TitleHeader> */}
            <GameTitleHeader type="slot" />
            <main className={"new-company-list slot"}>
                {
                    exportNewSlotJson.map((comp, index) => {
                        return (
                            <SlotItems key={index}
                                isLogin={User?.is_authenticated ? true : false}
                                SetLoginPop={SetLoginPop}
                                company={comp} />
                        )
                    })
                }
            </main>
        </div>
    );
}

function SlotItems({ company, isLogin, SetLoginPop }: { company: SlotImageInterface, isLogin: boolean, SetLoginPop: Function }) {
    const { SetLobbyShow, SetVendor, SetType } = useContext(lobbyModalContext)
    const [audioPlay, setAudioPlay] = useState<boolean>(false)
    return (
        <div
            onClick={e => {
                if (isLogin) {
                    // SetLoadingPop(true)
                    if (['popok',].includes(company.vendor)) {
                        swal({
                            text: '점검중 입니다.',
                            icon: 'warning'
                        })
                    } else if (company.vendor == 'Booongo') {
                        window.open(`/gameLobby/Booongo/?uid=bng_mini_lobby&vendor=Booongo`,
                            // window.open(`https://slot-cu88.com/gameLobby/Booongo/?uid=bng_mini_lobby&vendor=Booongo`,
                            `${company.vendor}_${company.id}`, `width=1200 height=640`)
                    }
                    else if (company.vendor == 'MicroGaming Plus Slo') {
                        window.open(`/gameLobby/MicroGaming Plus Slo/?vendor=MicroGaming%20Plus%20Slo&uid=falconlobby`,
                            `${company.vendor}_${company.id}`, `width=1200 height=640`)
                    } else {
                        SetVendor(company.vendor)
                        SetType('slot')
                        SetLobbyShow()
                    }
                    // ErrorVendorCheck(company.vendor, company.id, 'slot')
                    //     .finally(()=> SetLoadingPop(false))
                } else {
                    SetLoginPop()
                }
            }}
        >
            <div className={'hover-cover'}
                onMouseOver={e => setAudioPlay(true)}
                onMouseOut={e => setAudioPlay(false)}
            ></div>
            <div className='chip'>
                <div className='inner'>
                    <img src={company.img} className={"original"} />
                    <div className={'focus-btn-slot'}>
                        <button>게임입장</button>
                    </div>
                </div>
            </div>
            <div className='game-logo'>
                <img src={company.logo} alt="" title={company.name} />
                <p>{company.name}</p>
            </div>
            <AudioPlayer type={"company"} play={audioPlay} />
        </div>
    )
}
